import { useState, useEffect, memo, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Alert, AlertTitle, AlertDescription, Text } from '@chakra-ui/react';
import { IRecordedInterviewEntry } from 'types';
import { Spinner } from 'shared-components';
import MyInterviewWidget from '../myInterviewWidget';
import { decrypt } from '../../../../../utilities/crypto';
import { getInterviewQuestions } from '../../../../../api/api';
import { hasUploadedPassport, missingPassportNotificationText } from '../helpers';
import { ApplicationState } from '../../../../../store';

interface IProps {
  studentNo: string;
  uni: string;
  interview: IRecordedInterviewEntry;
  interviewHash: string;
  jobId: string;
  interviewAttemptsCount: number;
  isExceeded: boolean;
  idToken: string;
}

export const MyInterviewItem: React.FC<IProps> = ({
  studentNo,
  uni,
  interview,
  interviewHash,
  jobId,
  interviewAttemptsCount,
  isExceeded,
  idToken
}) => {
  const { interviewName, recorded } = interview;
  const passport = useSelector((state: ApplicationState) => state.student.documents.passport);

  const renewPassports = Object.keys(passport.renew ?? {}).length > 0;

  const activePassport =
    renewPassports && hasUploadedPassport(passport.renew ?? {}) ? passport.renew : passport.current;

  const InterviewDescription = () => {
    let blockDescription = '';
    if (isExceeded) {
      blockDescription = 'This interview is no longer available.';
    } else if (recorded) {
      blockDescription = 'Your recorded video interview is complete.';
    } else {
      blockDescription = 'You have been asked to complete a recorded video interview.';
    }
    return blockDescription;
  };

  let retryQuestions;
  if (typeof interview.questions === 'string') {
    const decryptQuestions = decrypt(interview.questions as string, 'MyInt', 'Winner');
    retryQuestions = JSON.parse(decryptQuestions!);
  } else {
    retryQuestions = interview.questions;
  }

  const [questions, setQuestions] = useState<Array<any>>(retryQuestions ?? []);
  const [encryptedQuestions, setEncryptedQuestions] = useState(retryQuestions ? (interview.questions as string) : '');
  const [welcomeTitle, setWelcomeTitle] = useState<string>(interview.welcomeMessageTitle ?? '');

  const isPassportUploaded = useMemo(() => hasUploadedPassport(activePassport ?? {}), [activePassport]);

  useEffect(() => {
    async function fetchData() {
      if (idToken !== '') {
        const response = await getInterviewQuestions(idToken, uni, studentNo, interview.interviewId);

        const { welcomeTitle, questions } = response.data;
        setWelcomeTitle(welcomeTitle);
        const decryptQuestions = decrypt(questions, 'MyInt', 'Winner');
        const parsedQuestions = JSON.parse(decryptQuestions!);
        setQuestions(parsedQuestions);
        setEncryptedQuestions(questions);
      }
    }
    if (questions.length === 0) {
      fetchData();
    }
  }, [idToken]);

  if (!questions.length) {
    return <Spinner />;
  }

  interview.questions = questions ?? [];

  const InterviewButton = () =>
    recorded ? (
      <div></div>
    ) : (
      <MyInterviewWidget
        interviewHash={interviewHash}
        interview={interview}
        jobId={jobId}
        questions={questions}
        encryptedQuestions={encryptedQuestions}
        welcomeTitle={welcomeTitle}
        interviewAttemptsCount={interviewAttemptsCount}
        isExceeded={isExceeded}
        isDisabled={!isPassportUploaded}
      />
    );

  const statusColor = () => {
    if (recorded) {
      return 'green';
    } else if (isExceeded) {
      return 'gray';
    } else {
      return 'orange';
    }
  };

  return (
    <Alert
      data-selector="interview-event"
      data-recorded-status={recorded}
      variant="status-block"
      colorScheme={statusColor()}
    >
      <AlertTitle as="h3">{interviewName}</AlertTitle>

      <AlertDescription>
        <InterviewDescription />
        {!isPassportUploaded && <Text mt={4}>{missingPassportNotificationText}</Text>}
      </AlertDescription>

      <InterviewButton />
    </Alert>
  );
};

export default memo(MyInterviewItem, (prevProps, nextProps) => {
  if (
    prevProps.interviewHash === nextProps.interviewHash &&
    prevProps.isExceeded === nextProps.isExceeded &&
    prevProps.interview.recorded === nextProps.interview.recorded
  ) {
    return true;
  }
  return false;
});
